import "./Guarantors.scss";
import React, { useMemo, useState } from "react";
import { FormOutlined } from "@ant-design/icons";
import { getApplicationSelector } from "@quest-finance/quest-fe-shared/dist/application";
import {
  GUARANTOR_ASSET_TYPES,
  GUARANTOR_ASSET_TYPE_LABELS,
} from "@quest-finance/quest-fe-shared/dist/application/constants/guarantorAssetTypes";
import { GUARANTOR_LIABILITY_TYPES_LABELS } from "@quest-finance/quest-fe-shared/dist/application/constants/guarantorLiabilityTypes";
import { GUARANTOR_RESIDENTIAL_STATUSES_OPTIONS } from "@quest-finance/quest-fe-shared/dist/application/constants/guarantorResidentialStatuses";
import { MARITAL_STATUS_LABELS } from "@quest-finance/quest-fe-shared/dist/application/constants/maritalStatuses";
import {
  GuarantorAsset,
  GuarantorLiability,
} from "@quest-finance/quest-fe-shared/dist/application/types/Guarantor";
import { LOADING_STATUS } from "@quest-finance/quest-fe-shared/dist/common/constants/loadingStatuses";
import { TITLE_OPTIONS } from "@quest-finance/quest-fe-shared/dist/common/constants/titles";
import { dateFormat } from "@quest-finance/quest-fe-shared/dist/common/utils/date";
import { convertToCurrency } from "@quest-finance/quest-fe-shared/dist/common/utils/number";
import { ERROR_CODES } from "@quest-finance/quest-fe-shared/dist/error-handler";
import { Col, Collapse, Divider, Popover, Row, Skeleton, Spin } from "antd";
import { AxiosError } from "axios";
import { differenceInYears } from "date-fns";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { Link } from "react-router";
import { CreditButtonMultiple } from "./credit-score/CreditButton";
import CreditScore from "./credit-score/CreditScore";
import { Guarantor, ApplicationResponse } from "../../../application";
import {
  showErrorPopUp,
  showSuccessPopUp,
} from "../../../common/components/ShowNotification/showNotification";
import {
  DATE_LIST_FORMAT,
  LONG_DATE_FORMAT,
} from "../../../common/contstants/app";
import { processError } from "../../../common/utils/error";
import { QfCollapse } from "../../../theme";
import { getAssessmentExtras } from "../../actions/creators/assessmentExtras";
import {
  GUARANTOR1_SCORE_KEY,
  GUARANTOR2_SCORE_KEY,
  GUARANTOR1_ILLION_KEY,
  GUARANTOR2_ILLION_KEY,
  GUARANTOR1_FRANKIEONE_KEY,
  GUARANTOR2_FRANKIEONE_KEY,
  GUARANTOR1_EQUIFAX_APPLY,
  GUARANTOR2_EQUIFAX_APPLY,
} from "../../constants/assessmentExtras";
import { useAssessmentExtrasDispatch } from "../../dispatchers";
import {
  assessmentExtrasDataSelector,
  assessmentExtrasStatusSelector,
} from "../../selectors/assessmentExtras";
import CreditService from "../../services/CreditService";
import { ScoreResponse } from "../../types/AssessmentExtras";
import { EquifaxApplyReportResponse } from "../../types/CreditReference";

type AssetsProps = {
  assets: GuarantorAsset[];
  guarantorIndex: number;
};

type LiabilitiesProps = {
  liabilities: GuarantorLiability[];
  guarantorIndex: number;
};

const Assets: React.FunctionComponent<AssetsProps> = ({
  assets,
  guarantorIndex,
}: AssetsProps) => {
  const typeLabels = GUARANTOR_ASSET_TYPE_LABELS;

  let total = 0;

  if (assets.length > 0) {
    total = assets
      .map((asset) => asset.amount)
      .reduce((previousValue, currentValue) => {
        return previousValue + currentValue;
      });
  }

  return (
    <div
      className="assets-liabilities"
      data-testid={`guarantor-${guarantorIndex}-assets`}
    >
      <table>
        <thead>
          <tr>
            <th>Assets</th>
            <th className="value">Value</th>
          </tr>
        </thead>
        <tbody>
          {assets.map((asset, index) => (
            <tr key={index}>
              <td
                data-testid={`guarantor-${guarantorIndex}-${index}-assets-type`}
              >
                <Popover
                  content={<AssetDetails asset={asset} />}
                  title={null}
                  placement="topLeft"
                >
                  <span>{typeLabels[asset.type] as string}</span>
                </Popover>
              </td>
              <td
                className="value"
                data-testid={`guarantor-${guarantorIndex}-${index}-assets-value`}
              >
                <NumericFormat
                  value={asset.amount}
                  prefix="$"
                  displayType="text"
                  fixedDecimalScale
                  thousandSeparator=","
                  decimalScale={2}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Divider className="sum qf-divider" />
      <table>
        <tbody>
          <tr>
            <td>Total Assets</td>
            <td
              className="value"
              data-testid={`guarantor-${guarantorIndex}-assets-total`}
            >
              <NumericFormat
                value={total}
                prefix="$"
                displayType="text"
                fixedDecimalScale
                thousandSeparator=","
                decimalScale={2}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

type LiabilityDetailsProps = {
  liability: GuarantorLiability;
};
type AssetDetailsProps = {
  asset: GuarantorAsset;
};
const LiabilityDetails: React.FunctionComponent<LiabilityDetailsProps> = ({
  liability,
}: LiabilityDetailsProps) => {
  const currencyOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  const _amount = "$" + convertToCurrency(liability.amount, currencyOptions);
  const _monthlyRepaymentAmount =
    "$" + convertToCurrency(liability.monthlyRepaymentAmount, currencyOptions);

  return (
    <ul className="guarantor-liabilities-details">
      <li>
        Liability type -{" "}
        <strong>
          {GUARANTOR_LIABILITY_TYPES_LABELS[liability.type] as string}
        </strong>
      </li>
      <li>
        Lender name - <strong>{liability.lenderName}</strong>
      </li>
      <li>
        Balance / credit limit - <strong>{_amount}</strong>
      </li>
      <li>
        Monthly repayment - <strong>{_monthlyRepaymentAmount}</strong>
      </li>
    </ul>
  );
};

const AssetDetails: React.FunctionComponent<AssetDetailsProps> = ({
  asset,
}: AssetDetailsProps) => {
  const currencyOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  const _amount = "$" + convertToCurrency(asset.amount, currencyOptions);
  return (
    <ul className="guarantor-asset-details">
      <li>
        Asset type -{" "}
        <strong>{GUARANTOR_ASSET_TYPE_LABELS[asset.type] as string}</strong>
      </li>
      <li>
        Description - <strong>{asset.description?.substring(0, 200)}</strong>
      </li>
      <li>
        Value - <strong>{_amount}</strong>
      </li>
    </ul>
  );
};
const Liabilities: React.FunctionComponent<LiabilitiesProps> = ({
  liabilities,
  guarantorIndex,
}: LiabilitiesProps) => {
  const typeLabels = GUARANTOR_LIABILITY_TYPES_LABELS;

  let total = 0;

  if (liabilities.length > 0) {
    total = liabilities
      .map((liability) => liability.amount)
      .reduce((previousValue, currentValue) => {
        return previousValue + currentValue;
      });
  }

  return (
    <div
      className="assets-liabilities"
      data-testid={`guarantor-${guarantorIndex}-liabilities`}
    >
      <table>
        <thead>
          <tr>
            <th>Liabilities</th>
            <th className="value">Value</th>
          </tr>
        </thead>
        <tbody>
          {liabilities.map((liability, index) => (
            <tr key={index}>
              <td
                data-testid={`guarantor-${guarantorIndex}-${index}-liabilities-type`}
              >
                <Popover
                  content={<LiabilityDetails liability={liability} />}
                  title={null}
                  placement="topLeft"
                >
                  <span>{typeLabels[liability.type] as string}</span>
                </Popover>
              </td>
              <td
                className="value"
                data-testid={`guarantor-${guarantorIndex}-${index}-liabilities-value`}
              >
                <Popover
                  content={<LiabilityDetails liability={liability} />}
                  title={null}
                  placement="topLeft"
                >
                  <span>
                    <NumericFormat
                      value={liability.amount}
                      prefix="$"
                      displayType="text"
                      fixedDecimalScale
                      thousandSeparator=","
                      decimalScale={2}
                    />
                  </span>
                </Popover>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Divider className="sum qf-divider" />
      <table>
        <tbody>
          <tr>
            <td>Total Liabilities</td>
            <td
              className="value"
              data-testid={`guarantor-${guarantorIndex}-liabilities-total`}
            >
              <NumericFormat
                value={total}
                prefix="$"
                displayType="text"
                fixedDecimalScale
                thousandSeparator=","
                decimalScale={2}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const { Panel } = Collapse;

const Guarantors: React.FunctionComponent = () => {
  const { id, guarantors, assessmentId } = useSelector(
    getApplicationSelector
  ) as ApplicationResponse;
  const assessmentExtrasDispatch = useAssessmentExtrasDispatch();
  const assessmentExtrasStatus = useSelector(assessmentExtrasStatusSelector);
  const assessmentExtras = useSelector(assessmentExtrasDataSelector);
  const [illionRequestLoading, setIllionRequestLoading] = useState(false);
  const [amlRequestLoading, setAmlRequestLoading] = useState(false);
  const [equifaxApplyLoading, setEquifaxApplyLoading] = useState(false);
  const entityIds = guarantors?.map((guarantor) => guarantor.entityId);
  const guarantorIllions = useMemo<
    {
      guarantorNo: string;
      lastRequest: string;
    }[]
  >(() => {
    const guarantorIllions: {
      guarantorNo: string;
      lastRequest: string;
    }[] = [];

    guarantors.forEach((_, index) => {
      const guarantorNo = index + 1;
      const illionData = assessmentExtras
        ? (assessmentExtras[
            `ILLION.CONSUMER_CREDIT.GUARANTOR_${guarantorNo}`
          ] as ScoreResponse) ?? null
        : null;

      const lastRequest =
        illionData && illionData.updatedAt
          ? dateFormat(new Date(illionData.updatedAt), DATE_LIST_FORMAT)
          : "";

      if (lastRequest) {
        guarantorIllions.push({
          guarantorNo: guarantorNo.toString(),
          lastRequest,
        });
      }
    });

    return guarantorIllions;
  }, [assessmentExtras, guarantors]);

  const guarantorFrankieOnes = useMemo<
    {
      guarantorNo: string;
      lastRequest: string;
    }[]
  >(() => {
    const guarantorFrankieOnes: {
      guarantorNo: string;
      lastRequest: string;
    }[] = [];

    guarantors.forEach((_, index) => {
      const guarantorNo = index + 1;
      const frankieOneData = assessmentExtras
        ? (assessmentExtras[
            `FRANKIEONE.CREATE_VERIFY.GUARANTOR_${guarantorNo}`
          ] as ScoreResponse) ?? null
        : null;

      const lastRequest =
        frankieOneData && frankieOneData.updatedAt
          ? dateFormat(new Date(frankieOneData.updatedAt), DATE_LIST_FORMAT)
          : "";

      if (lastRequest) {
        guarantorFrankieOnes.push({
          guarantorNo: guarantorNo.toString(),
          lastRequest,
        });
      }
    });

    return guarantorFrankieOnes;
  }, [assessmentExtras, guarantors]);

  const guarantorEquifax = useMemo<
    {
      guarantorNo: string;
      lastRequest: string;
    }[]
  >(() => {
    const guarantorEquifax: {
      guarantorNo: string;
      lastRequest: string;
    }[] = [];

    guarantors.forEach((_, index) => {
      const guarantorNo = index + 1;
      const equifaxData = assessmentExtras
        ? (assessmentExtras[
            `EQUIFAX.COMPANY_APPLY.GUARANTOR_${guarantorNo}`
          ] as ScoreResponse) ?? null
        : null;

      const lastRequest =
        equifaxData && equifaxData.updatedAt
          ? dateFormat(new Date(equifaxData.updatedAt), DATE_LIST_FORMAT)
          : "";

      if (lastRequest) {
        guarantorEquifax.push({
          guarantorNo: guarantorNo.toString(),
          lastRequest,
        });
      }
    });

    return guarantorEquifax;
  }, [assessmentExtras, guarantors]);

  const netAssets = (guarantor: Guarantor) => {
    let totalAssets = 0;
    if (guarantor.assets.length > 0) {
      totalAssets = guarantor.assets
        .map((asset) => asset.amount)
        .reduce((previousValue, currentValue) => {
          return previousValue + currentValue;
        });
    }

    let totalLiabilities = 0;
    if (guarantor.liabilities.length > 0) {
      totalLiabilities = guarantor.liabilities
        .map((liability) => liability.amount)
        .reduce((previousValue, currentValue) => {
          return previousValue + currentValue;
        });
    }

    const netPosition = totalAssets - totalLiabilities;

    return netPosition;
  };

  const renderInvestmentPropertyAddress = (guarantor: Guarantor) => {
    const address = [
      guarantor.investmentPropertyAddressUnitNumber,
      guarantor.investmentPropertyAddressStreetNumber,
      guarantor.investmentPropertyAddressStreetName,
      guarantor.investmentPropertyAddressSuburb,
      guarantor.investmentPropertyAddressState,
      guarantor.investmentPropertyAddressPostcode,
    ]
      .filter((address) => !!address)
      .join(" ");

    if (address) {
      return (
        (guarantor.investmentPropertyAddressUnitNumber
          ? `${guarantor.investmentPropertyAddressUnitNumber}/`
          : "") +
        [
          guarantor.investmentPropertyAddressStreetNumber,
          guarantor.investmentPropertyAddressStreetName,
          guarantor.investmentPropertyAddressSuburb,
          guarantor.investmentPropertyAddressState,
          guarantor.investmentPropertyAddressPostcode,
        ]
          .filter((address) => !!address)
          .join(" ")
      );
    }

    return "-";
  };

  let content = <Skeleton active />;

  if (guarantors && guarantors.length) {
    content = (
      <QfCollapse
        defaultActiveKey={["guarantor-0", "guarantor-1"]}
        className="collapsible"
        expandIconPosition="end"
        ghost
      >
        {guarantors.map((guarantor, index) => {
          const guarantorNo = index + 1;

          return (
            <Panel
              header={
                <span data-testid={`guarantor-${index}-header`}>{`Guarantor ${
                  index + 1
                } - ${TITLE_OPTIONS[guarantor.title as string]} ${
                  guarantor.firstName
                } ${guarantor.middleName ?? ""} ${guarantor.lastName}`}</span>
              }
              key={`guarantor-${index}`}
              className="panel application-data guarantors-data"
              data-testid={`guarantor-${index}`}
            >
              <CreditScore
                assessmentId={assessmentId as string}
                guarantor={guarantor}
                guarantorNo={guarantorNo}
                amlKycAllLoading={amlRequestLoading}
                illionAllLoading={illionRequestLoading}
                equifaxAllLoading={equifaxApplyLoading}
              />
              <div className="section-header">Personal Info</div>
              <Row className="info">
                <Col span={24} md={8}>
                  <span className="label">Drivers licence</span> <br />
                  <span className="value">
                    <span
                      data-testid={`guarantor-${index}-driverLicenseNumber`}
                    >
                      {guarantor.driverLicenseNumber ?? "-"}
                    </span>{" "}
                    <span data-testid={`guarantor-${index}-driverLicenseState`}>
                      {guarantor.driverLicenseState
                        ? `(${guarantor.driverLicenseState})`
                        : ""}
                    </span>
                  </span>
                </Col>
                <Col span={24} md={16}>
                  <span className="label">Licence Card number</span> <br />
                  <span
                    className="value"
                    data-testid={`guarantor-${index}-driverLicenseCardNumber`}
                  >
                    {guarantor.driverLicenseCardNumber ?? "-"}
                  </span>
                </Col>
              </Row>
              <Row className="info">
                <Col md={8} sm={12}>
                  <span className="label">DOB|Age</span> <br />
                  <span className="value">
                    <span data-testid={`guarantor-${index}-dateOfBirth`}>
                      {dateFormat(
                        new Date(guarantor.dateOfBirth as string),
                        LONG_DATE_FORMAT
                      )}
                    </span>{" "}
                    (
                    <span data-testid={`guarantor-${index}-age`}>
                      {differenceInYears(
                        new Date(),
                        new Date(guarantor.dateOfBirth as string)
                      )}
                    </span>{" "}
                    yrs)
                  </span>
                </Col>
                <Col md={16} sm={12}>
                  <span className="label">Marital status</span> <br />
                  <span className="value">
                    <span data-testid={`guarantor-${index}-maritalStatus`}>
                      {
                        MARITAL_STATUS_LABELS[
                          guarantor.maritalStatus as string
                        ] as string
                      }
                    </span>{" "}
                    <span data-testid={`guarantor-${index}-dependentNumber`}>
                      {guarantor.dependentNumber
                        ? `w/ ${guarantor.dependentNumber}`
                        : ""}
                    </span>
                  </span>
                </Col>
              </Row>
              <Row className="info">
                <Col md={8} sm={12}>
                  <span className="label">Mobile</span> <br />
                  <span
                    className="value"
                    data-testid={`guarantor-${index}-mobile`}
                  >
                    {guarantor.mobile}
                  </span>
                </Col>
                <Col md={16} sm={12}>
                  <span className="label">Email</span> <br />
                  <span
                    className="value"
                    data-testid={`guarantor-${index}-email`}
                  >
                    {guarantor.email as string}
                  </span>
                </Col>
              </Row>
              <Row className="info">
                <Col md={8} sm={12}>
                  <span className="label">Residential status</span> <br />
                  <span
                    className="value"
                    data-testid={`guarantor-${index}-residentialStatus`}
                  >
                    {
                      GUARANTOR_RESIDENTIAL_STATUSES_OPTIONS[
                        guarantor.residentialStatus as string
                      ] as string
                    }
                  </span>
                </Col>
                <Col md={16} sm={12}>
                  <span className="label">Residential address</span> <br />
                  <span
                    className="value"
                    data-testid={`guarantor-${index}-residentialAddress`}
                  >
                    {guarantor.addressUnitNumber
                      ? `${guarantor.addressUnitNumber}/`
                      : ""}
                    {[
                      guarantor.addressStreetNumber,
                      guarantor.addressStreetName,
                      guarantor.addressSuburb,
                      guarantor.addressState,
                      guarantor.addressPostcode,
                    ]
                      .filter((address) => !!address)
                      .join(" ")}
                  </span>
                </Col>
              </Row>
              {guarantor.assets.findIndex(
                (asset) =>
                  asset.type === GUARANTOR_ASSET_TYPES.INVESTMENT_PROPERTY
              ) !== -1 && (
                <Row className="info">
                  <Col md={8} sm={12}>
                    <span className="label">Property status</span> <br />
                    <span className="value">Invesment</span>
                  </Col>
                  <Col md={16} sm={12}>
                    <span className="label">
                      Invesment property address optional
                    </span>{" "}
                    <br />
                    <span
                      className="value"
                      data-testid={`guarantor-${index}-investmentPropertyAddress`}
                    >
                      {renderInvestmentPropertyAddress(guarantor)}
                    </span>
                  </Col>
                </Row>
              )}
              <div className="section-header">Assets and Liabilities</div>
              <Row gutter={[32, 16]}>
                <Col md={12}>
                  <Assets assets={guarantor.assets} guarantorIndex={index} />
                </Col>
                <Col md={12}>
                  <Liabilities
                    liabilities={guarantor.liabilities}
                    guarantorIndex={index}
                  />
                </Col>
              </Row>

              <Row gutter={[32, 16]}>
                <Col md={12}>
                  <Divider className="sum total" />
                  <table
                    style={{
                      width: "100%",
                      tableLayout: "fixed",
                    }}
                    className="net-assets"
                  >
                    <tbody>
                      <tr>
                        <td>Net assets</td>
                        <td
                          style={{ textAlign: "right" }}
                          data-testid={`guarantor-${index}-netAssets`}
                        >
                          <NumericFormat
                            value={netAssets(guarantor)}
                            prefix="$"
                            displayType="text"
                            fixedDecimalScale
                            thousandSeparator=","
                            decimalScale={2}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
              </Row>
            </Panel>
          );
        })}
      </QfCollapse>
    );
  }

  const generateIllionCCR = async () => {
    if (!assessmentId) return;
    setIllionRequestLoading(true);
    try {
      await CreditService.getIllionReport(assessmentId, entityIds);
      assessmentExtrasDispatch(
        getAssessmentExtras(assessmentId, {
          [GUARANTOR1_SCORE_KEY]: ["illion"],
          [GUARANTOR2_SCORE_KEY]: ["illion"],
          [GUARANTOR1_ILLION_KEY]: ["score"],
          [GUARANTOR2_ILLION_KEY]: ["score"],
        })
      );
      showSuccessPopUp(
        `Successfully retrieved score/documents for all guarantors`
      );
    } catch (error) {
      const apiError = error as AxiosError;
      if (apiError.response?.data.errorCode === ERROR_CODES.TOO_MANY_REQUESTS) {
        showErrorPopUp(
          "An ongoing request is still in progress. It will be completed short while."
        );
      } else {
        processError(error, (errorMessage) => {
          showErrorPopUp(errorMessage);
        });
      }
    } finally {
      setIllionRequestLoading(false);
    }
  };

  const generateAMLandKYC = async () => {
    if (!assessmentId) return;
    setAmlRequestLoading(true);
    try {
      await CreditService.getFrankieOneReport(
        assessmentId as string,
        entityIds
      );

      assessmentExtrasDispatch(
        getAssessmentExtras(assessmentId, {
          [GUARANTOR1_FRANKIEONE_KEY]: ["score", "entity_id", "status"],
          [GUARANTOR2_FRANKIEONE_KEY]: ["score", "entity_id", "status"],
        })
      );
      showSuccessPopUp(
        `Successfully retrieved score/documents for all guarantors`
      );
    } catch (error) {
      const apiError = error as AxiosError;
      if (apiError.response?.data.errorCode === ERROR_CODES.TOO_MANY_REQUESTS) {
        showErrorPopUp(
          "An ongoing request is still in progress. It will be completed short while."
        );
      } else {
        processError(error, (errorMessage) => {
          showErrorPopUp(errorMessage);
        });
      }
    } finally {
      setAmlRequestLoading(false);
    }
  };

  const generateEquifaxApplyReport = async () => {
    if (!assessmentId) return;
    setEquifaxApplyLoading(true);
    try {
      const response = (await CreditService.getEquifaxApplyReport(
        assessmentId,
        entityIds
      )) as EquifaxApplyReportResponse;

      const errorData = response.data.errors;
      if (errorData.length > 0) {
        const error = errorData[0];
        error.errors.forEach((err, index) => {
          const key = `error${index}`;
          showErrorPopUp(err, key);
        });
      } else {
        assessmentExtrasDispatch(
          getAssessmentExtras(assessmentId, {
            [GUARANTOR1_SCORE_KEY]: ["equifax"],
            [GUARANTOR2_SCORE_KEY]: ["equifax"],
            [GUARANTOR1_EQUIFAX_APPLY]: ["score"],
            [GUARANTOR2_EQUIFAX_APPLY]: ["score"],
          })
        );
        showSuccessPopUp(
          `Successfully retrieved score/documents for all guarantors`
        );
      }
    } catch (error) {
      const apiError = error as AxiosError;
      if (apiError.response?.data.errorCode === ERROR_CODES.TOO_MANY_REQUESTS) {
        showErrorPopUp(
          "An ongoing request is still in progress. It will be completed short while."
        );
      } else {
        processError(error, (errorMessage) => {
          showErrorPopUp(errorMessage);
        });
      }
    } finally {
      setEquifaxApplyLoading(false);
    }
  };

  return (
    <div className="guarantors-wrapper">
      <Spin spinning={assessmentExtrasStatus === LOADING_STATUS.LOADING}>
        <div className="header border-0">
          <div className="header-title">
            <div className="label">
              Guarantors{" "}
              <Link to={`/application/applications/${id}/guarantors`}>
                <FormOutlined className="edit-link" />
              </Link>
            </div>
          </div>
          <div className="credit-buttons-header">
            <CreditButtonMultiple
              className="equifax"
              getPopupContainer={() =>
                document.querySelector(".guarantors-wrapper") as HTMLElement
              }
              onClick={generateEquifaxApplyReport}
              data={guarantorEquifax}
              requestLoading={equifaxApplyLoading}
              popUpPlacement="topRight"
              label="Run all Equifax Apply"
            />
            <CreditButtonMultiple
              className="illion"
              getPopupContainer={() =>
                document.querySelector(".guarantors-wrapper") as HTMLElement
              }
              onClick={generateIllionCCR}
              data={guarantorIllions}
              requestLoading={illionRequestLoading}
              popUpPlacement="topRight"
              label="Run all Illion CCR"
            />
            <CreditButtonMultiple
              className="aml-kyc"
              getPopupContainer={() =>
                document.querySelector(".guarantors-wrapper") as HTMLElement
              }
              onClick={generateAMLandKYC}
              data={guarantorFrankieOnes}
              requestLoading={amlRequestLoading}
              popUpPlacement="topRight"
              label="Run all AML/KYC"
            />
          </div>
        </div>
        {content}
      </Spin>
    </div>
  );
};

export default Guarantors;
